import { Injectable } from '@angular/core';
import {
	Notification,
	NotificationAction,
	RemoteData,
	appData,
} from '@lycoris-nubila/ngx-lycoris';
import { Observable, from, of } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';

import { NotificationDomain } from 'application/common/model/notification-domain';
import { UserUsageStatsOutput } from 'infrastructure/graphql/messaging/user/model/user-usage-stats.output';
import { UserApiPort } from 'user/port/user-api.port';
import { IUserUsageStats } from 'user/user-usage-stats';
import { UserUsageStatsConverterService } from 'user/user-usage-stats-converter.service';

import { AppPushApiPort } from './port/app-push-api.port';
import { AppSharedApplicationPort } from './port/app-shared-application.port';
import { AppSharedRepositoryPort } from './port/app-shared-repository.port';

@Injectable()
export class AppSharedApplicationAdapter implements AppSharedApplicationPort {
	constructor(
		private _userApiPort: UserApiPort,
		private _appPushApiPort: AppPushApiPort,
		private _appSharedRepositoryPort: AppSharedRepositoryPort,
		private _userUsageStatsConverterService: UserUsageStatsConverterService
	) {
		this._appPushApiPort
			.observeDomainMessage<null>(NotificationDomain.USER_USAGE_STATS)
			.subscribe((message) => this._handleMessage(message));
	}

	public observePendingMessages(
		authorsId: string[]
	): Observable<RemoteData<{ [key: string]: number }>> {
		const api$ = from(this._userApiPort.fetchUserPendingMessages(authorsId)).pipe(
			tap((data) => this._appSharedRepositoryPort.saveUserPendingMessages(data)),
			map(
				(data) =>
					({
						data: data,
						fresh: true,
					} as RemoteData<{ [key: string]: number }>)
			),
			catchError((_) =>
				of({
					fresh: true,
					data: null,
				} as RemoteData<{ [key: string]: number }>)
			),
			startWith({ fresh: false, data: null } as RemoteData<{
				[key: string]: number;
			}>)
		);

		const repo$ = this._appSharedRepositoryPort.observeUserPendingMessages();

		return appData(api$, repo$);
	}

	public observeUserUsageStats(): Observable<RemoteData<IUserUsageStats>> {
		const api$ = from(
			Promise.all([
				this._userApiPort.fetchUserUsageStats().catch(() => null),
				this._userApiPort.fetchUserStatistics().catch(() => null),
			]).then(([iam, message]) =>
				this._userUsageStatsConverterService.userUsageStatAsEntity(iam, message)
			)
		).pipe(
			tap((stats) => this._appSharedRepositoryPort.saveUserUsageStats(stats)),
			map(
				(stats) =>
					({
						data: stats,
						fresh: true,
					} as RemoteData<UserUsageStatsOutput>)
			),
			catchError((_) =>
				of({ fresh: true, data: null } as RemoteData<UserUsageStatsOutput>)
			),
			startWith({ fresh: false, data: null } as RemoteData<UserUsageStatsOutput>)
		);

		const repo$ = this._appSharedRepositoryPort.observeUserUsageStats();

		return appData(api$, repo$);
	}

	private _handleMessage<T>(message: Notification<NotificationDomain, T>) {
		switch (message.action) {
			case NotificationAction.ADD:
			case NotificationAction.UPDATE:
				from(
					Promise.all([
						this._userApiPort.fetchUserUsageStats().catch(() => null),
						this._userApiPort.fetchUserStatistics().catch(() => null),
					]).then(([iam, message]) =>
						this._userUsageStatsConverterService.userUsageStatAsEntity(iam, message)
					)
				)
					.pipe(
						tap((stats) => this._appSharedRepositoryPort.saveUserUsageStats(stats))
					)
					.subscribe();
				break;
			case NotificationAction.DELETE:
				break;
		}
	}
}
